/**
 * Earnings Forecast Dashboard — FIE Screen 1
 * Apple-level, Stripe-level premium financial interface.
 * Progressive disclosure. Breakdown-first. Zero clutter.
 */
import { useState, useMemo } from "react";
import { useFIE } from "@/data/fie/context";
import { FIETrustHeader } from "./FIETrustHeader";
import { BreakdownPanel } from "./BreakdownPanel";
import { AttributionLedgerDrawer } from "./AttributionLedgerDrawer";
import { LockDistributeFlow } from "./LockDistributeFlow";
import { UnresolvedItemsPanel } from "./UnresolvedItemsPanel";
import { ProofPanel } from "./ProofPanel";
import { ConfidenceDetail, CompletenessBadge } from "./SharedComponents";
import type { FinancialAttribution } from "@/data/fie/attributionTypes";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";

function fmtCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
}

interface LedgerState {
  open: boolean;
  attributions: FinancialAttribution[];
  title: string;
  subtitle: string;
}

export function EarningsForecastDashboard() {
  const {
    visibleForecast: f, isPeriodLocked, selectedPeriodId,
    canViewCosts, canViewEconomics,
  } = useFIE();

  const locked = isPeriodLocked(selectedPeriodId);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [explainMode, setExplainMode] = useState(false);
  const [ledger, setLedger] = useState<LedgerState>({
    open: false, attributions: [], title: "", subtitle: "",
  });
  const [lockFlowOpen, setLockFlowOpen] = useState(false);

  // Collect all attributions for the "Open Ledger" button
  const allAttributions = useMemo(() => {
    const attrs: FinancialAttribution[] = [];
    if (f.revenueLines) attrs.push(...f.revenueLines.flatMap(r => r.attributions));
    if (f.costs) attrs.push(...f.costs.flatMap(c => c.attributions));
    return attrs;
  }, [f.revenueLines, f.costs]);

  const openLedger = (attributions: FinancialAttribution[], title: string, subtitle: string) => {
    setLedger({ open: true, attributions, title, subtitle });
  };

  const unresolvedCount = useMemo(() => {
    let count = 0;
    if (f.revenueLines) f.revenueLines.forEach(r => { count += r.lineage.unresolvedItems?.length || 0; });
    if (f.costs) f.costs.forEach(c => { count += c.lineage.unresolvedItems?.length || 0; });
    return count;
  }, [f.revenueLines, f.costs]);

  return (
    <div className="space-y-6">
      {/* Sticky Trust Header */}
      <FIETrustHeader
        onToggleBreakdown={() => setBreakdownOpen(!breakdownOpen)}
        onOpenLedger={allAttributions.length > 0
          ? () => openLedger(allAttributions, "Full Period Ledger", `All attributions · ${allAttributions.length} records`)
          : undefined
        }
        breakdownOpen={breakdownOpen}
        onExplainThis={canViewEconomics ? () => setExplainMode(!explainMode) : undefined}
        explainActive={explainMode}
        onLockDistribute={canViewEconomics && !locked ? () => setLockFlowOpen(true) : undefined}
      />

      {/* Explain This Number — formula-style breakdown */}
      {explainMode && canViewEconomics && (
        <div className="bg-card rounded-xl border border-border/40 p-6 shadow-sm animate-in slide-in-from-top-2 duration-300">
          <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground mb-4">How this number is calculated</p>
          <div className="space-y-2">
            <ExplainRow label="Revenue" value={f.revenue?.expected} onDrill={f.revenueLines?.length ? () => openLedger(f.revenueLines!.flatMap(r => r.attributions), "Revenue", "All revenue attributions") : undefined} />
            {canViewCosts && f.costs && (
              <>
                <div className="border-t border-border/20 my-1" />
                {f.costs.map(c => (
                  <ExplainRow key={c.category} label={`– ${c.category.charAt(0).toUpperCase() + c.category.slice(1)}`} value={-c.blended} isCost onDrill={c.attributions.length ? () => openLedger(c.attributions, c.category, fmtCurrency(c.blended)) : undefined} />
                ))}
              </>
            )}
            <div className="border-t border-foreground/20 my-1" />
            <div className="flex items-baseline justify-between">
              <p className="text-[15px] font-bold">= Net Profit</p>
              <p className="text-[18px] font-bold tabular-nums tracking-tight">{fmtCurrency(f.netProfit?.expected || 0)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Breakdown Panel — inline expand */}
      {breakdownOpen && (
        <div className="bg-card rounded-xl border border-border/40 p-6 shadow-sm">
          <BreakdownPanel
            forecast={f}
            canViewCosts={canViewCosts}
            canViewEconomics={canViewEconomics}
            onOpenLedger={openLedger}
          />
        </div>
      )}

      {/* Unresolved Items — only show if there are issues */}
      {unresolvedCount > 0 && <UnresolvedItemsPanel forecast={f} />}

      {/* Confidence + Completeness — subtle, not dominant */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ConfidenceDetail metadata={f.confidence} />
        <CompletenessBadge completeness={f.completeness} />
      </div>

      {/* Proof Panel */}
      <ProofPanel forecast={f} locked={locked} />

      {/* Attribution Ledger Drawer */}
      <AttributionLedgerDrawer
        open={ledger.open}
        onOpenChange={(open) => setLedger(prev => ({ ...prev, open }))}
        attributions={ledger.attributions}
        title={ledger.title}
        subtitle={ledger.subtitle}
      />

      {/* Lock & Distribute Flow */}
      <LockDistributeFlow open={lockFlowOpen} onClose={() => setLockFlowOpen(false)} />
    </div>
  );
}

// ── Explain Row ──
function ExplainRow({ label, value, isCost, onDrill }: {
  label: string; value?: number; isCost?: boolean; onDrill?: () => void;
}) {
  if (value === undefined) return null;
  return (
    <button
      onClick={onDrill}
      disabled={!onDrill}
      className={cn(
        "w-full flex items-baseline justify-between py-1.5 group text-left",
        onDrill && "hover:text-foreground cursor-pointer"
      )}
    >
      <span className={cn("text-[13px]", isCost ? "text-muted-foreground" : "font-medium")}>{label}</span>
      <span className="flex items-center gap-1.5">
        <span className={cn("text-[14px] tabular-nums", isCost ? "text-muted-foreground" : "font-semibold")}>
          {fmtCurrency(Math.abs(value))}
        </span>
        {onDrill && <ChevronRight className="h-3 w-3 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors" />}
      </span>
    </button>
  );
}
